import React from "react";
import { Link, useLocation } from "react-router-dom";
import "../styles/ordersummary.css";
import { formatMoney } from "./formatMoney";
import { useSelector } from "react-redux";
import { calculateDiscount } from "../actions/couponActions";

const OrderSummary = ({ cartTotal = 0, orders = [], appliedCoupons = [] }) => {
  const location = useLocation();
  const isCartPage = location.pathname === "/cart";
  const singleItem = location.state?.singleItem;

  // Calculate total based on what's available
  const total = singleItem
    ? singleItem.price * singleItem.quantity
    : orders.length > 0
    ? orders.reduce((sum, order) => sum + Number(order.total_price || 0), 0)
    : cartTotal;

  // Get shipping fees from Redux store
  const { shippingFees } = useSelector((state) => state.shippingFees);
  const { items } = useSelector((state) => state.cart);

  // Calculate total weight from cart items
  const totalWeight =
    items && items.length > 0
      ? items.reduce((total, item) => {
          const itemWeight = item?.product?.weight || 1;
          return total + (itemWeight * item?.quantity || 0);
        }, 0)
      : 0;

  // For orders, use the shipping fee from the order data if available
  let shippingFee = 0;
  let hasShippingFee = false;

  if (orders.length > 0) {
    // Get shipping fee from the first order (assuming all orders in the array are related)
    shippingFee = orders[0]?.shipping_fees
      ? parseFloat(orders[0].shipping_fees)
      : 0;
    hasShippingFee = !!orders[0]?.shipping_fees;
  } else {
    // Find applicable shipping fee based on weight for cart items
    let applicableFee = null;
    if (shippingFees && totalWeight > 0) {
      let feesToCheck = Array.isArray(shippingFees)
        ? shippingFees
        : shippingFees.results || [];

      if (feesToCheck.length > 0) {
        applicableFee = feesToCheck.find((fee) => {
          const minWeight = parseFloat(fee.min_weight);
          const maxWeight = parseFloat(fee.max_weight);
          return totalWeight >= minWeight && totalWeight <= maxWeight;
        });
      }
    }

    shippingFee = applicableFee
      ? parseFloat(applicableFee.shipping_fee || 0)
      : 0;
    hasShippingFee = !!applicableFee;
  }

  shippingFee = 0;
  const grandTotal = total + shippingFee;

  // Calculate total discount from applied coupons
  const totalDiscount = appliedCoupons.reduce((discountTotal, coupon) => {
    // For orders, use the stored discount amount directly
    if (orders.length > 0 && coupon.discount !== undefined) {
      return discountTotal + parseFloat(coupon.discount);
    }
    // For cart, calculate discount using coupon rules
    return discountTotal + calculateDiscount(coupon, total);
  }, 0);

  const finalTotal = grandTotal - totalDiscount;

  return (
    <div className="order-summary">
      <h4>Order Summary</h4>
      <div className="summary-detail">
        <span>Subtotal:</span>
        <span>{formatMoney(total)}</span>
      </div>
      <div className="summary-detail">
        <span>Shipping Fee:</span>
        {orders.length > 0 ? (
          // For orders, always display the shipping fee from order data
          <span className="order-summary-shipping-amount">
            {formatMoney(shippingFee)}
          </span>
        ) : hasShippingFee ? (
          // For cart, display the calculated fee if available
          <span className="order-summary-shipping-amount">
            {formatMoney(shippingFee)}
          </span>
        ) : (
          // Otherwise show processing or TBD
          <span>{totalWeight > 0 ? "Processing..." : "TBD"}</span>
        )}
      </div>

      {/* Coupon Discounts */}
      {appliedCoupons.length > 0 && (
        <div className="summary-detail coupon-discounts">
          <span>Coupon Discount:</span>
          <span className="discount-amount">-{formatMoney(totalDiscount)}</span>
        </div>
      )}

      <div className="summary-total">
        <strong>Total:</strong>
        <strong>{formatMoney(finalTotal)}</strong>
      </div>

      {isCartPage && (
        <Link to="/checkout">
          <button className="checkout-button">Proceed to Checkout</button>
        </Link>
      )}
    </div>
  );
};

export default OrderSummary;
